import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { clientsAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { success: showSuccess, error: showError } = useToast();

  // Fetch client data
  const {
    data: clientData,
    isLoading: clientLoading,
    error: clientError
  } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsAPI.getClient(id!),
    enabled: !!id,
  });

  const client = (clientData as any)?.data?.data?.client;
  const tasks = (clientData as any)?.data?.data?.tasks || [];
  const summary = (clientData as any)?.data?.data?.summary;

  if (clientLoading) return <LoadingSpinner />;

  if (clientError || !client) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load client details. Please try again.</p>
          <Link
            to="/clients"
            className="mt-2 inline-block bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Back to Clients
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/clients"
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Clients
          </Link>
        </div>
        <div className="flex gap-3">
          <Link
            to={`/clients/${id}/edit`}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PencilIcon className="w-4 h-4 mr-2" />
            Edit Client
          </Link>
        </div>
      </div>

      {/* Client Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <UserIcon className="w-8 h-8 text-gray-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {client.firstName} {client.lastName}
              </h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <PhoneIcon className="w-4 h-4 mr-2" />
                  {client.phone}
                </div>
                <div className="flex items-center">
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  {client.email}
                </div>
                <div className="flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-2" />
                  {client.address?.city}, {client.address?.state}
                </div>
                <div className="flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Added {new Date(client.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Full Name</label>
                <p className="text-gray-900">{client.firstName} {client.lastName}</p>
              </div>
              {client.dateOfBirth && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                  <p className="text-gray-900">{new Date(client.dateOfBirth).toLocaleDateString()}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-500">Contact Information</label>
                <p className="text-gray-900">{client.email}</p>
                <p className="text-gray-900">{client.phone}</p>
              </div>
            </div>
            <div className="space-y-4">
              {client.address && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Address</label>
                  <p className="text-gray-900">
                    {client.address.street}<br />
                    {client.address.city}, {client.address.state} {client.address.zip}
                  </p>
                </div>
              )}
              {client.gender && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Gender</label>
                  <p className="text-gray-900">{client.gender}</p>
                </div>
              )}
              {client.financialInfo?.budgetRange && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Budget Range</label>
                  <p className="text-gray-900">{client.financialInfo.budgetRange}</p>
                </div>
              )}
            </div>
          </div>

          {/* Emergency Contact */}
          {client.emergencyContact && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="text-sm font-medium text-gray-500">Emergency Contact</label>
              <div className="mt-2 bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 font-medium">{client.emergencyContact.name}</p>
                <p className="text-gray-600 text-sm">{client.emergencyContact.relationship}</p>
                <p className="text-gray-600 text-sm">{client.emergencyContact.phone}</p>
              </div>
            </div>
          )}

          {/* Medical Notes */}
          {client.medicalNotes && client.medicalNotes.trim() && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="text-sm font-medium text-gray-500">Medical Notes</label>
              <div className="mt-2 bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 whitespace-pre-wrap">{client.medicalNotes}</p>
              </div>
            </div>
          )}

          {/* Notes */}
          {client.notes && client.notes.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="text-sm font-medium text-gray-500">Notes</label>
              <div className="mt-2 space-y-3">
                {client.notes.map((note: any, index: number) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
                    {note.createdAt && (
                      <p className="text-gray-500 text-xs mt-2">
                        {new Date(note.createdAt).toLocaleDateString()} {new Date(note.createdAt).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
